import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, KeyRound, AlertCircle, UserPlus, User, Phone, Play, X, Shield, Zap, Smartphone, Scale, RefreshCw, BarChart3, Users, Headphones, FileText, Clock, CheckCircle, Star, Heart, Globe, Key, Wallet, TrendingUp, Award, Briefcase } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { areFunctionalCookiesAllowed, setFunctionalStorage } from '../../components/common/CookieConsent';
import { clearTokens } from '../../utils/authTokens';
import BrandMark from '../../components/BrandMark';
import PartnerMark, { getInitials } from '../../components/PartnerMark';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CLIENT_STORAGE_KEY = 'willkart_client_remembered_email';
const TRUSTED_STORAGE_KEY = 'willkart_trusted_remembered_email';

// Icon mapping for dynamic rendering
const ICON_MAP = {
  Shield, Lock, Zap, Smartphone, Scale, RefreshCw, BarChart3, Users, Headphones,
  FileText, Clock, CheckCircle, Star, Heart, Globe, Key, Wallet, TrendingUp, Award, Briefcase
};

// Default features if not customized
const DEFAULT_LOGIN_FEATURES = [
  { id: '1', icon: 'Shield', title: 'Secure Vault', description: 'Store all your important documents in one encrypted, protected space', enabled: true, order: 1 },
  { id: '2', icon: 'Zap', title: 'Quick Setup', description: 'Create your will in minutes with our guided step-by-step process', enabled: true, order: 2 },
  { id: '3', icon: 'Smartphone', title: 'Access Anywhere', description: 'View and manage your documents from any device, anytime', enabled: true, order: 3 },
  { id: '4', icon: 'Scale', title: 'Legally Valid', description: 'Documents crafted to meet all legal requirements and standards', enabled: true, order: 4 },
  { id: '5', icon: 'RefreshCw', title: 'Easy Updates', description: 'Modify your will anytime as your life circumstances change', enabled: true, order: 5 },
  { id: '6', icon: 'BarChart3', title: 'Track Progress', description: 'Monitor your will completion status with clear visual indicators', enabled: true, order: 6 },
  { id: '7', icon: 'Users', title: 'Trusted Contacts', description: 'Share access with family members for emergency situations', enabled: true, order: 7 },
  { id: '8', icon: 'Lock', title: 'Bank-Grade Security', description: 'Enterprise-level encryption protects your sensitive data', enabled: true, order: 8 },
  { id: '9', icon: 'Headphones', title: 'Expert Support', description: 'Get help from our team whenever you need assistance', enabled: true, order: 9 },
];

const ClientLogin = ({ subdomainFromHost }) => {
  const { subdomain: subdomainFromParams } = useParams();
  const subdomain = subdomainFromHost || subdomainFromParams; // Prefer host-based subdomain
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { executeRecaptcha } = useGoogleReCaptcha();
  
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Login video state
  const [loginVideo, setLoginVideo] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  
  // Login features state (What You'll Get section)
  const [loginFeatures, setLoginFeatures] = useState({
    enabled: true,
    section_title: "What You'll Get",
    cards: DEFAULT_LOGIN_FEATURES
  });
  
  // Login mode: 'client' or 'trusted'
  const [loginMode, setLoginMode] = useState('client');
  
  const [step, setStep] = useState('email'); // 'email' | 'password' | 'set_password' | 'forgot_password' | 'reset_password' | 'register'
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [resetToken, setResetToken] = useState(null);
  
  // Trusted contact state
  const [trustedStep, setTrustedStep] = useState('login'); // 'login' | 'forgot_password' | 'setup'
  const [trustedSetupToken, setTrustedSetupToken] = useState(null);
  
  // Remember me state
  const [isReturningClient, setIsReturningClient] = useState(false);
  const [isReturningTrusted, setIsReturningTrusted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const passwordInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    mobile: '',
    password: '',
    confirmPassword: '',
  });

  // Clear any stale client session on login page mount.
  // Prevents a previous user's token from bypassing login on a shared browser.
  // The remembered-email keys (CLIENT_STORAGE_KEY / TRUSTED_STORAGE_KEY) are
  // intentionally preserved so the "Welcome back" card still works.
  useEffect(() => {
    clearTokens('client');
    localStorage.removeItem('clientId');
    localStorage.removeItem('clientName');
    localStorage.removeItem('clientEmail');
    // Note: clientSubdomain is kept so redirect logic works if needed
  }, []); // runs once on mount

  // Check for remembered emails on mount (respects cookie preferences)
  useEffect(() => {
    if (!areFunctionalCookiesAllowed()) return;
    
    const savedClientEmail = localStorage.getItem(CLIENT_STORAGE_KEY);
    const savedTrustedEmail = localStorage.getItem(TRUSTED_STORAGE_KEY);
    
    if (savedClientEmail) {
      setIsReturningClient(true);
      if (loginMode === 'client') {
        setFormData(prev => ({ ...prev, email: savedClientEmail }));
      }
    }
    if (savedTrustedEmail) {
      setIsReturningTrusted(true);
      if (loginMode === 'trusted') {
        setFormData(prev => ({ ...prev, email: savedTrustedEmail }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle switching accounts
  const handleSwitchClientAccount = () => {
    localStorage.removeItem(CLIENT_STORAGE_KEY);
    setFormData(prev => ({ ...prev, email: '', password: '' }));
    setIsReturningClient(false);
    setStep('email');
  };

  const handleSwitchTrustedAccount = () => {
    localStorage.removeItem(TRUSTED_STORAGE_KEY);
    setFormData(prev => ({ ...prev, email: '', password: '' }));
    setIsReturningTrusted(false);
    setTrustedStep('login');
  };

  // Check for trusted_setup query param (from invitation email)
  useEffect(() => {
    const isTrustedSetup = searchParams.get('trusted_setup') === 'true';
    const token = searchParams.get('token');
    
    if (isTrustedSetup && token) {
      setLoginMode('trusted');
      setTrustedStep('setup');
      setTrustedSetupToken(token);
    }
  }, [searchParams]);

  // Fetch partner info by subdomain
  useEffect(() => {
    const fetchPartnerInfo = async () => {
      try {
        const response = await axios.get(`${API}/subdomain/${subdomain}`);
        setPartnerInfo(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.detail || 'Partner not found');
        setLoading(false);
      }
    };

    if (subdomain) {
      fetchPartnerInfo();
    }
  }, [subdomain]);

  // Fetch login video
  useEffect(() => {
    const fetchLoginVideo = async () => {
      try {
        const response = await axios.get(`${API}/videos/login`);
        if (response.data.video) {
          setLoginVideo(response.data.video);
        }
      } catch (err) {
        // Silent fail - video is optional
        console.error('Failed to fetch login video:', err);
      }
    };
    fetchLoginVideo();
  }, []);

  // Fetch login page features (What You'll Get section)
  useEffect(() => {
    const fetchLoginFeatures = async () => {
      try {
        const response = await axios.get(`${API}/login-features`);
        if (response.data) {
          setLoginFeatures(response.data);
        }
      } catch (err) {
        // Silent fail - use defaults
        console.error('Failed to fetch login features:', err);
      }
    };
    fetchLoginFeatures();
  }, []);

  // Extract YouTube video ID
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Get dashboard URL based on routing mode (subdomain vs path-based)
  const getDashboardUrl = () => {
    return '/dashboard';
  };

  // Fetch partner info by subdomain
  useEffect(() => {
    const fetchPartnerInfo = async () => {
      try {
        const response = await axios.get(`${API}/subdomain/${subdomain}`);
        setPartnerInfo(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.detail || 'Partner not found');
        setLoading(false);
      }
    };

    if (subdomain) {
      fetchPartnerInfo();
    }
  }, [subdomain]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error('Please enter your email');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/client/login`, {
        email: formData.email,
        password: '',
        subdomain: subdomain,
      });

      if (response.data.requires_email_verification) {
        // Walk-in client needs to verify email first
        setClientData({ 
          email: response.data.email, 
          name: response.data.client_name 
        });
        setStep('verification_pending');
        toast.info('Please verify your email before logging in.');
      } else if (response.data.requires_password_setup) {
        // First time login - partner registered client
        setClientData(response.data.client);
        setStep('set_password');
        toast.info('Welcome! Please create your password.');
      } else if (response.data.requires_registration) {
        // Email not found - offer walk-in registration
        setStep('register');
        toast.info('New here? Please register to continue.');
      } else if (response.data.requires_password) {
        // Email exists and has password - go to password step
        setStep('password');
      }
    } catch (err) {
      const message = err.response?.data?.detail;
      toast.error(message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle walk-in registration
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    if (formData.name.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }

    if (!/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (!formData.mobile || !/^\d{10}$/.test(formData.mobile.trim().replace(/[\s\-\+]/g, '').slice(-10))) {
      toast.error('Mobile number must be 10 digits');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (!termsAccepted) {
      toast.error('Please accept the Terms & Conditions and Privacy Policy');
      return;
    }

    if (!executeRecaptcha) {
      toast.error('reCAPTCHA not ready. Please try again.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Get reCAPTCHA v3 token
      const recaptchaToken = await executeRecaptcha('client_register');
      
      const response = await axios.post(`${API}/client/register`, {
        email: formData.email,
        name: formData.name.trim(),
        mobile: formData.mobile,
        password: formData.password,
        subdomain: subdomain,
        recaptcha_token: recaptchaToken,
      });

      // Check if email verification is required (walk-in client)
      if (response.data.requires_email_verification) {
        setClientData({ 
          email: response.data.email, 
          name: response.data.client_name 
        });
        setStep('verification_pending');
        toast.success('Registration successful! Please check your email to verify your account.');
      } else if (response.data.token) {
        // Fallback for non-walk-in flow
        localStorage.setItem('clientToken', response.data.token);

        if (response.data.refresh_token) {

          localStorage.setItem('clientAccessToken', response.data.access_token || response.data.token);

          localStorage.setItem('clientRefreshToken', response.data.refresh_token);

        }
        localStorage.setItem('clientEmail', formData.email);
        localStorage.setItem('clientId', response.data.client.id);
        localStorage.setItem('clientName', response.data.client.name);
        localStorage.setItem('clientSubdomain', subdomain);
        toast.success('Registration successful! Welcome!');
        navigate(getDashboardUrl());
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail) ? detail.map(e => e.msg?.replace('Value error, ', '')).join(', ') : detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle resend verification email
  const handleResendVerification = async () => {
    if (!clientData?.email) {
      toast.error('No email found. Please try logging in again.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/client/resend-verification`, {
        email: clientData.email,
        subdomain: subdomain,
      });

      toast.success(response.data.message || 'Verification email sent!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to resend verification email';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!formData.password) {
      toast.error('Please enter your password');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/client/login`, {
        email: formData.email,
        password: formData.password,
        subdomain: subdomain,
      });

      if (response.data.token) {
        // Save email for next visit (respects cookie preferences)
        setFunctionalStorage(CLIENT_STORAGE_KEY, formData.email);
        localStorage.setItem('clientToken', response.data.token);

        if (response.data.refresh_token) {

          localStorage.setItem('clientAccessToken', response.data.access_token || response.data.token);

          localStorage.setItem('clientRefreshToken', response.data.refresh_token);

        }
        localStorage.setItem('clientEmail', formData.email);
        localStorage.setItem('clientId', response.data.client.id);
        localStorage.setItem('clientName', response.data.client.name);
        localStorage.setItem('clientSubdomain', subdomain);
        sessionStorage.setItem('client_just_logged_in', 'true');
        toast.success('Welcome back!');
        // Redirect to client dashboard (to be built)
        navigate(getDashboardUrl());
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid credentials';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetPasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (!termsAccepted) {
      toast.error('Please accept the Terms & Conditions and Privacy Policy');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/client/set-password`, {
        email: formData.email,
        password: formData.password,
        subdomain: subdomain,
      });

      if (response.data.token) {
        // Save email for next visit (respects cookie preferences)
        setFunctionalStorage(CLIENT_STORAGE_KEY, formData.email);
        localStorage.setItem('clientToken', response.data.token);

        if (response.data.refresh_token) {

          localStorage.setItem('clientAccessToken', response.data.access_token || response.data.token);

          localStorage.setItem('clientRefreshToken', response.data.refresh_token);

        }
        localStorage.setItem('clientEmail', formData.email);
        localStorage.setItem('clientId', response.data.client.id);
        localStorage.setItem('clientName', response.data.client.name);
        localStorage.setItem('clientSubdomain', subdomain);
        sessionStorage.setItem('client_just_logged_in', 'true');
        toast.success('Account setup complete! Welcome!');
        // Redirect to client dashboard (to be built)
        navigate(getDashboardUrl());
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to set password';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/client/forgot-password`, {
        email: formData.email,
        subdomain: subdomain,
      });
      
      // Show success message - email will be sent with magic link
      toast.success('If an account exists with this email, you will receive a password reset link shortly.');
      setStep('email'); // Go back to email step
      setFormData((prev) => ({ ...prev, email: '', password: '', confirmPassword: '' }));
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to process request. Please try again.';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/client/reset-password`, {
        reset_token: resetToken,
        new_password: formData.password,
      });

      if (response.data.token) {
        // Save email for next visit (respects cookie preferences)
        setFunctionalStorage(CLIENT_STORAGE_KEY, formData.email);
        localStorage.setItem('clientToken', response.data.token);

        if (response.data.refresh_token) {

          localStorage.setItem('clientAccessToken', response.data.access_token || response.data.token);

          localStorage.setItem('clientRefreshToken', response.data.refresh_token);

        }
        localStorage.setItem('clientEmail', formData.email);
        localStorage.setItem('clientId', response.data.client.id);
        localStorage.setItem('clientName', response.data.client.name);
        localStorage.setItem('clientSubdomain', subdomain);
        sessionStorage.setItem('client_just_logged_in', 'true');
        toast.success('Password reset successfully! Welcome back!');
        navigate(getDashboardUrl());
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================
  // Trusted Contact Login Handlers
  // ============================================
  
  const handleTrustedLogin = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error('Please enter email and password');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/trusted/login`, {
        email: formData.email,
        password: formData.password,
        subdomain: subdomain || ''
      });

      // Save email for next visit (respects cookie preferences)
      setFunctionalStorage(TRUSTED_STORAGE_KEY, formData.email);
      localStorage.setItem('trustedToken', response.data.token);

      if (response.data.refresh_token) {

        localStorage.setItem('trustedAccessToken', response.data.access_token || response.data.token);

        localStorage.setItem('trustedRefreshToken', response.data.refresh_token);

      }
      localStorage.setItem('trustedName', response.data.trusted_contact.name);
      localStorage.setItem('trustedEmail', response.data.trusted_contact.email);
      
      toast.success('Login successful!');
      navigate('/trusted/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTrustedForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error('Please enter your email');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/trusted/forgot-password`, {
        email: formData.email,
        subdomain: subdomain || ''
      });
      
      toast.success('If an account exists, you will receive a reset link.');
      setTrustedStep('login');
    } catch (error) {
      toast.success('If an account exists, you will receive a reset link.');
      setTrustedStep('login');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle trusted contact password setup (from invitation email)
  const handleTrustedSetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/trusted/set-password`, {
        token: trustedSetupToken,
        password: formData.password
      });

      // Save email for next visit (respects cookie preferences)
      setFunctionalStorage(TRUSTED_STORAGE_KEY, response.data.trusted_contact.email);
      localStorage.setItem('trustedToken', response.data.token);

      if (response.data.refresh_token) {

        localStorage.setItem('trustedAccessToken', response.data.access_token || response.data.token);

        localStorage.setItem('trustedRefreshToken', response.data.refresh_token);

      }
      localStorage.setItem('trustedName', response.data.trusted_contact.name);
      localStorage.setItem('trustedEmail', response.data.trusted_contact.email);
      
      toast.success('Account activated successfully!');
      navigate('/trusted/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to set password';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when switching modes
  const handleModeSwitch = (mode) => {
    setLoginMode(mode);
    setStep('email');
    setTrustedStep('login');
    setTrustedSetupToken(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    
    // Load remembered email for the selected mode (respects cookie preferences)
    if (areFunctionalCookiesAllowed()) {
      if (mode === 'client') {
        const savedEmail = localStorage.getItem(CLIENT_STORAGE_KEY);
        setFormData({ email: savedEmail || '', name: '', mobile: '', password: '', confirmPassword: '' });
      } else {
        const savedEmail = localStorage.getItem(TRUSTED_STORAGE_KEY);
        setFormData({ email: savedEmail || '', name: '', mobile: '', password: '', confirmPassword: '' });
      }
    } else {
      setFormData({ email: '', name: '', mobile: '', password: '', confirmPassword: '' });
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Page Not Found</h1>
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 lg:p-8">
        <div className="w-full max-w-4xl flex flex-col lg:flex-row items-stretch gap-6">
          {/* Left Side - Video Section */}
          {loginVideo && getYouTubeVideoId(loginVideo.youtube_url) && (
            <div className="w-full lg:w-1/2" data-testid="login-video-container">
              <div 
                className="relative w-full h-full min-h-[280px] rounded-xl overflow-hidden bg-slate-900/50 border border-slate-800 shadow-xl"
              >
                {!isVideoPlaying ? (
                  // Thumbnail/Play Button State with YouTube Thumbnail
                  <button
                    onClick={() => setIsVideoPlaying(true)}
                    className="absolute inset-0 flex flex-col items-center justify-center gap-3 group overflow-hidden"
                    data-testid="login-video-play-btn"
                  >
                    {/* YouTube Thumbnail Background - using hqdefault as it's more reliable */}
                    <img 
                      src={`https://img.youtube.com/vi/${getYouTubeVideoId(loginVideo.youtube_url)}/hqdefault.jpg`}
                      alt={loginVideo.video_title || 'Video thumbnail'}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        // Fallback to mqdefault if hqdefault doesn't exist
                        e.target.src = `https://img.youtube.com/vi/${getYouTubeVideoId(loginVideo.youtube_url)}/mqdefault.jpg`;
                      }}
                    />
                    {/* Dark overlay for better contrast */}
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-300" />
                    
                    {/* Play button */}
                    <div className="relative z-10 w-16 h-16 rounded-full bg-red-600 flex items-center justify-center shadow-lg shadow-red-600/40 group-hover:scale-110 group-hover:bg-red-500 transition-all duration-300">
                      <Play size={28} className="text-white ml-1" fill="white" />
                    </div>
                    <span className="relative z-10 text-white text-sm font-medium px-4 text-center drop-shadow-lg">
                      {loginVideo.video_title || 'Watch: How WillKart Works'}
                    </span>
                  </button>
                ) : (
                  // Video Playing State
                  <div className="absolute inset-0">
                    {/* Close button */}
                    <button
                      onClick={() => setIsVideoPlaying(false)}
                      className="absolute top-3 right-3 z-10 text-white/70 hover:text-white bg-black/50 hover:bg-black/70 transition-colors p-2 rounded-full"
                      title="Close video"
                      data-testid="login-video-close-btn"
                    >
                      <X size={18} />
                    </button>
                    
                    {/* YouTube iFrame with privacy settings */}
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${getYouTubeVideoId(loginVideo.youtube_url)}?rel=0&modestbranding=1&fs=0&iv_load_policy=3&disablekb=1&playsinline=1&autoplay=1`}
                      title={loginVideo.video_title || 'WillKart Introduction'}
                      className="absolute inset-0 w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      referrerPolicy="strict-origin-when-cross-origin"
                      loading="lazy"
                    />
                    
                    {/* Overlay to hide YouTube branding */}
                    <div 
                      className="absolute bottom-0 right-0 w-32 h-12 bg-transparent cursor-default"
                      style={{ pointerEvents: 'auto' }}
                      onClick={(e) => e.preventDefault()}
                    />
                    <div 
                      className="absolute top-0 left-0 w-24 h-10 bg-transparent cursor-default"
                      style={{ pointerEvents: 'auto' }}
                      onClick={(e) => e.preventDefault()}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Right Side - Login Form Container */}
          <div className={`w-full ${loginVideo && getYouTubeVideoId(loginVideo.youtube_url) ? 'lg:w-1/2' : 'max-w-md mx-auto'}`}>
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 h-full">
              {/* Logo & Partner Name */}
              <div className="text-center mb-6">
                {partnerInfo?.logo ? (
                  <img
                    src={partnerInfo.logo.startsWith('http') ? partnerInfo.logo : `${BACKEND_URL}${partnerInfo.logo}`}
                alt={partnerInfo.partner_name}
                className="h-20 w-auto mx-auto mb-4 object-contain"
              />
            ) : (
              <div className="flex justify-center mx-auto mb-4">
                <PartnerMark partnerInfo={partnerInfo} size="xl" showName={false} />
              </div>
            )}
            <h1 className="text-2xl font-bold text-white">{partnerInfo?.partner_name}</h1>
          </div>

          {/* Login Mode Tabs */}
          <div className="flex bg-slate-800/50 rounded-xl p-1 mb-4">
            <button
              type="button"
              onClick={() => handleModeSwitch('client')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                loginMode === 'client'
                  ? 'bg-amber-500 text-slate-900'
                  : 'text-slate-400 hover:text-white'
              }`}
              data-testid="client-tab"
            >
              <User className="w-4 h-4" />
              Client
            </button>
            <button
              type="button"
              onClick={() => handleModeSwitch('trusted')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                loginMode === 'trusted'
                  ? 'bg-amber-500 text-slate-900'
                  : 'text-slate-400 hover:text-white'
              }`}
              data-testid="trusted-tab"
            >
              <Shield className="w-4 h-4" />
              Trusted Person
            </button>
          </div>

          {/* Login Card */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8">
            
            {/* ============================================ */}
            {/* CLIENT LOGIN FORMS */}
            {/* ============================================ */}
            {loginMode === 'client' && (
              <>
            {/* Email Step */}
            {step === 'email' && (
              <form onSubmit={handleEmailSubmit} className="space-y-6" data-testid="email-form">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-white">
                    {isReturningClient ? 'Welcome back' : 'Welcome'}
                  </h2>
                </div>

                <div>
                  {isReturningClient && formData.email ? (
                    <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                        <span className="text-amber-400 font-semibold">
                          {formData.email.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-400 text-sm truncate">{formData.email}</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleSwitchClientAccount}
                        className="text-amber-400 hover:text-amber-300 text-sm whitespace-nowrap"
                        data-testid="client-switch-account-btn"
                      >
                        Not you?
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email"
                        className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                        data-testid="client-email-input"
                        autoFocus
                      />
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold h-12 rounded-xl"
                  data-testid="email-submit-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900"></div>
                      Checking...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Continue
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </Button>
              </form>
            )}

            {/* Password Step */}
            {step === 'password' && (
              <form onSubmit={handlePasswordSubmit} className="space-y-6" data-testid="password-form">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-semibold text-white">Welcome Back</h2>
                  <p className="text-slate-400 text-sm mt-1">{formData.email}</p>
                </div>

                <div>
                  <Label htmlFor="password" className="text-slate-300 mb-2 block">
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      autoComplete="current-password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                      data-testid="client-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setFormData((prev) => ({ ...prev, password: '' }));
                    }}
                    className="text-sm text-slate-400 hover:text-white"
                  >
                    ← Change email
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep('forgot_password')}
                    className="text-sm text-amber-400 hover:text-amber-300"
                  >
                    Forgot password?
                  </button>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold h-12 rounded-xl"
                  data-testid="password-submit-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900"></div>
                      Signing in...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Sign In
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </Button>
              </form>
            )}

            {/* Set Password Step (First Time) */}
            {step === 'set_password' && (
              <form onSubmit={handleSetPasswordSubmit} className="space-y-6" data-testid="set-password-form">
                <div className="text-center mb-6">
                  <div className="p-3 bg-amber-500/10 rounded-xl inline-block mb-3">
                    <KeyRound className="w-6 h-6 text-amber-400" />
                  </div>
                  <h2 className="text-xl font-semibold text-white">Create Password</h2>
                  <p className="text-slate-400 text-sm mt-1">
                    Welcome {clientData?.name}! Set your password to continue.
                  </p>
                </div>

                <div>
                  <Label htmlFor="password" className="text-slate-300 mb-2 block">
                    New Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      autoComplete="new-password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a password (min 8 characters)"
                      className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                      data-testid="set-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-slate-300 mb-2 block">
                    Confirm Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      autoComplete="new-password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm your password"
                      className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Terms & Conditions Checkbox */}
                <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <Checkbox
                    id="client-terms-checkbox"
                    checked={termsAccepted}
                    onCheckedChange={setTermsAccepted}
                    className="mt-0.5 border-slate-500 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                    data-testid="client-terms-checkbox"
                  />
                  <label htmlFor="client-terms-checkbox" className="text-sm text-slate-300 cursor-pointer select-none">
                    By clicking Create Account, you agree to our{' '}
                    <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 underline">
                      Terms & Conditions
                    </a>
                    {' '}&{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 underline">
                      Privacy Policy
                    </a>
                  </label>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setFormData({ email: '', name: '', mobile: '', password: '', confirmPassword: '' });
                    setClientData(null);
                    setTermsAccepted(false);
                  }}
                  className="text-sm text-slate-400 hover:text-white"
                >
                  ← Back to login
                </button>

                <Button
                  type="submit"
                  disabled={isSubmitting || !termsAccepted}
                  className={`w-full font-semibold h-12 rounded-xl ${
                    termsAccepted 
                      ? 'bg-amber-500 hover:bg-amber-400 text-slate-900' 
                      : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                  }`}
                  data-testid="set-password-submit-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900"></div>
                      Setting up...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Create Account
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </Button>
              </form>
            )}

            {/* Walk-in Registration Step */}
            {step === 'register' && (
              <form onSubmit={handleRegisterSubmit} className="space-y-5" data-testid="register-form">
                <div className="text-center mb-6">
                  <div className="p-3 bg-emerald-500/10 rounded-xl inline-block mb-3">
                    <UserPlus className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h2 className="text-xl font-semibold text-white">Create Your Account</h2>
                  <p className="text-slate-400 text-sm mt-1">
                    Register with {partnerInfo?.partner_name} to get started
                  </p>
                </div>

                {/* Email (read-only) */}
                <div>
                  <Label className="text-slate-300 mb-2 block">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      type="email"
                      value={formData.email}
                      disabled
                      className="pl-10 bg-slate-800/30 border-slate-700 text-slate-400 h-12"
                    />
                  </div>
                </div>

                {/* Name */}
                <div>
                  <Label htmlFor="name" className="text-slate-300 mb-2 block">Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                      data-testid="register-name-input"
                    />
                  </div>
                </div>

                {/* Mobile */}
                <div>
                  <Label htmlFor="mobile" className="text-slate-300 mb-2 block">Mobile Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="mobile"
                      name="mobile"
                      type="tel"
                      required
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="Enter your mobile number"
                      className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                      data-testid="register-mobile-input"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <Label htmlFor="password" className="text-slate-300 mb-2 block">Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a password (min 8 characters)"
                      className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <Label htmlFor="confirmPassword" className="text-slate-300 mb-2 block">Confirm Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm your password"
                      className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                      data-testid="register-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Terms & Conditions Checkbox */}
                <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <Checkbox
                    id="terms-checkbox"
                    checked={termsAccepted}
                    onCheckedChange={setTermsAccepted}
                    className="mt-0.5 border-slate-500 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="terms-checkbox" className="text-sm text-slate-300 cursor-pointer select-none">
                    By clicking Register, you agree to our{' '}
                    <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 underline">
                      Terms & Conditions
                    </a>
                    {' '}&{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 underline">
                      Privacy Policy
                    </a>
                  </label>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setFormData({ email: '', name: '', mobile: '', password: '', confirmPassword: '' });
                    setTermsAccepted(false);
                  }}
                  className="text-sm text-slate-400 hover:text-white"
                >
                  ← Back to login
                </button>

                <Button
                  type="submit"
                  disabled={isSubmitting || !termsAccepted}
                  className={`w-full font-semibold h-12 rounded-xl ${
                    termsAccepted 
                      ? 'bg-emerald-500 hover:bg-emerald-400 text-white' 
                      : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                  }`}
                  data-testid="register-submit-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Creating account...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Register & Continue
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </Button>

                {/* reCAPTCHA disclaimer */}
                <p className="text-xs text-slate-500 text-center mt-2">
                  This site is protected by reCAPTCHA and the Google{' '}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-400">Privacy Policy</a>{' '}
                  and{' '}
                  <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-400">Terms of Service</a>{' '}
                  apply.
                </p>
              </form>
            )}

            {/* Forgot Password Step */}
            {step === 'forgot_password' && (
              <form onSubmit={handleForgotPassword} className="space-y-6" data-testid="forgot-password-form">
                <div className="text-center mb-6">
                  <div className="p-3 bg-blue-500/10 rounded-xl inline-block mb-3">
                    <Mail className="w-6 h-6 text-blue-400" />
                  </div>
                  <h2 className="text-xl font-semibold text-white">Reset Password</h2>
                  <p className="text-slate-400 text-sm mt-1">Enter your registered email to continue</p>
                </div>

                <div>
                  <Label htmlFor="email" className="text-slate-300 mb-2 block">
                    Email Address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setStep('password')}
                  className="text-sm text-slate-400 hover:text-white"
                >
                  ← Back to login
                </button>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold h-12 rounded-xl"
                  data-testid="forgot-submit-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900"></div>
                      Verifying...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Continue
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </Button>
              </form>
            )}

            {/* Reset Password Step */}
            {step === 'reset_password' && (
              <form onSubmit={handleResetPassword} className="space-y-6" data-testid="reset-password-form">
                <div className="text-center mb-6">
                  <div className="p-3 bg-emerald-500/10 rounded-xl inline-block mb-3">
                    <KeyRound className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h2 className="text-xl font-semibold text-white">Create New Password</h2>
                  <p className="text-slate-400 text-sm mt-1">
                    Hi {clientData?.name}! Choose a strong password.
                  </p>
                </div>

                <div>
                  <Label htmlFor="password" className="text-slate-300 mb-2 block">
                    New Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a password (min 8 characters)"
                      className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-slate-300 mb-2 block">
                    Confirm New Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm your password"
                      className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                      data-testid="reset-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setFormData({ email: '', password: '', confirmPassword: '' });
                    setResetToken(null);
                    setClientData(null);
                  }}
                  className="text-sm text-slate-400 hover:text-white"
                >
                  ← Back to login
                </button>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold h-12 rounded-xl"
                  data-testid="reset-submit-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900"></div>
                      Resetting...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Reset Password
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </Button>
              </form>
            )}

            {/* Email Verification Pending Step */}
            {step === 'verification_pending' && (
              <div className="text-center space-y-6" data-testid="verification-pending-step">
                <div className="p-4 bg-emerald-500/10 rounded-xl inline-block">
                  <Mail className="w-10 h-10 text-emerald-400" />
                </div>
                
                <div>
                  <h2 className="text-xl font-semibold text-white mb-2">Verify Your Email</h2>
                  <p className="text-slate-400 text-sm">
                    We have sent a verification link to
                  </p>
                  <p className="text-white font-medium mt-1">{clientData?.email}</p>
                </div>
                
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-left">
                  <p className="text-emerald-400 font-medium mb-2">Check Your Email!</p>
                  <p className="text-slate-400 text-sm">
                    Click the link in the email to verify your account and start using the platform.
                  </p>
                </div>
                
                <div className="bg-slate-800/50 rounded-lg p-4 text-left space-y-2">
                  <p className="text-slate-300 text-sm font-medium">Did not receive the email?</p>
                  <ul className="text-slate-400 text-xs space-y-1 list-disc list-inside">
                    <li>Check your spam or junk folder</li>
                    <li>Make sure <span className="text-white">{clientData?.email}</span> is correct</li>
                    <li>Wait a few minutes for delivery</li>
                  </ul>
                </div>
                
                <Button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={isSubmitting}
                  variant="outline"
                  className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10 h-12"
                  data-testid="resend-verification-btn"
                >
                  {isSubmitting ? 'Sending...' : 'Resend Verification Email'}
                </Button>
                
                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setFormData({ email: '', name: '', mobile: '', password: '', confirmPassword: '' });
                    setClientData(null);
                  }}
                  className="text-sm text-slate-400 hover:text-white block mx-auto"
                >
                  ← Back to login
                </button>
              </div>
            )}
              </>
            )}

            {/* ============================================ */}
            {/* TRUSTED CONTACT LOGIN FORMS */}
            {/* ============================================ */}
            {loginMode === 'trusted' && (
              <>
                {/* Trusted Login Form */}
                {trustedStep === 'login' && (
                  <form onSubmit={handleTrustedLogin} className="space-y-6" data-testid="trusted-login-form">
                    <div className="text-center mb-6">
                      <div className="p-3 bg-amber-500/10 rounded-xl inline-block mb-3">
                        <Shield className="w-6 h-6 text-amber-400" />
                      </div>
                      <h2 className="text-xl font-semibold text-white">
                        {isReturningTrusted ? 'Welcome back' : 'Trusted Contact Login'}
                      </h2>
                      <p className="text-slate-400 text-sm mt-1">Access documents shared with you</p>
                    </div>

                    <div>
                      <Label htmlFor="trusted-email" className="text-slate-300 mb-2 block">Email</Label>
                      {isReturningTrusted && formData.email ? (
                        <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                            <span className="text-amber-400 font-semibold">
                              {formData.email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-slate-400 text-sm truncate">{formData.email}</p>
                          </div>
                          <button
                            type="button"
                            onClick={handleSwitchTrustedAccount}
                            className="text-amber-400 hover:text-amber-300 text-sm whitespace-nowrap"
                            data-testid="trusted-switch-account-btn"
                          >
                            Not you?
                          </button>
                        </div>
                      ) : (
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                          <Input
                            id="trusted-email"
                            name="email"
                            type="email"
                            required
                            autoComplete="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter your email"
                            className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                            data-testid="trusted-email-input"
                            autoFocus
                          />
                        </div>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="trusted-password" className="text-slate-300 mb-2 block">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                        <Input
                          ref={isReturningTrusted ? passwordInputRef : null}
                          id="trusted-password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          required
                          autoComplete="current-password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Enter your password"
                          className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                          data-testid="trusted-password-input"
                          autoFocus={isReturningTrusted}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold h-12 rounded-xl"
                      data-testid="trusted-login-btn"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900"></div>
                          Signing in...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          Sign In
                          <ArrowRight className="w-5 h-5" />
                        </span>
                      )}
                    </Button>

                    <button
                      type="button"
                      onClick={() => setTrustedStep('forgot_password')}
                      className="w-full text-center text-amber-400 hover:text-amber-300 text-sm"
                    >
                      Forgot your password?
                    </button>
                  </form>
                )}

                {/* Trusted Forgot Password Form */}
                {trustedStep === 'forgot_password' && (
                  <form onSubmit={handleTrustedForgotPassword} className="space-y-6" data-testid="trusted-forgot-form">
                    <div className="text-center mb-6">
                      <div className="p-3 bg-blue-500/10 rounded-xl inline-block mb-3">
                        <Mail className="w-6 h-6 text-blue-400" />
                      </div>
                      <h2 className="text-xl font-semibold text-white">Reset Password</h2>
                      <p className="text-slate-400 text-sm mt-1">Enter your email to receive a reset link</p>
                    </div>

                    <div>
                      <Label htmlFor="forgot-email" className="text-slate-300 mb-2 block">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                        <Input
                          id="forgot-email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter your email"
                          className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                          data-testid="trusted-forgot-email-input"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setTrustedStep('login')}
                      className="text-sm text-slate-400 hover:text-white"
                    >
                      ← Back to login
                    </button>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold h-12 rounded-xl"
                      data-testid="trusted-forgot-btn"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900"></div>
                          Sending...
                        </span>
                      ) : (
                        'Send Reset Link'
                      )}
                    </Button>
                  </form>
                )}

                {/* Trusted Password Setup Form (from invitation email) */}
                {trustedStep === 'setup' && (
                  <form onSubmit={handleTrustedSetPassword} className="space-y-6" data-testid="trusted-setup-form">
                    <div className="text-center mb-6">
                      <div className="p-3 bg-emerald-500/10 rounded-xl inline-block mb-3">
                        <KeyRound className="w-6 h-6 text-emerald-400" />
                      </div>
                      <h2 className="text-xl font-semibold text-white">Set Up Your Account</h2>
                      <p className="text-slate-400 text-sm mt-1">Create a password to access shared documents</p>
                    </div>

                    <div>
                      <Label htmlFor="setup-password" className="text-slate-300 mb-2 block">Create Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                        <Input
                          id="setup-password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          required
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Minimum 8 characters"
                          className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                          data-testid="trusted-setup-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="setup-confirm-password" className="text-slate-300 mb-2 block">Confirm Password</Label>
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                        <Input
                          id="setup-confirm-password"
                          name="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          required
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Confirm your password"
                          className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 h-12"
                          data-testid="trusted-setup-confirm-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                        >
                          {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold h-12 rounded-xl"
                      data-testid="trusted-setup-btn"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900"></div>
                          Activating...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          Activate Account
                          <ArrowRight className="w-5 h-5" />
                        </span>
                      )}
                    </Button>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
        </div>
      </div>
      </div>

      {/* What You'll Get Section */}
      {loginFeatures.enabled && loginFeatures.cards.filter(c => c.enabled).length > 0 && (
        <section className="py-16 px-4" data-testid="what-youll-get-section">
          <div className="max-w-6xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                {loginFeatures.section_title || "What You'll Get"}
              </h2>
              <div className="w-16 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto rounded-full"></div>
            </div>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loginFeatures.cards
                .filter(card => card.enabled)
                .sort((a, b) => a.order - b.order)
                .map((card) => {
                  const IconComponent = ICON_MAP[card.icon] || Shield;
                  return (
                    <div
                      key={card.id}
                      className="group bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:bg-slate-800/50 hover:border-slate-600/50 transition-all duration-300"
                      data-testid={`feature-card-${card.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                          <IconComponent className="w-6 h-6 text-amber-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-semibold text-base mb-1">
                            {card.title}
                          </h3>
                          <p className="text-slate-400 text-sm leading-relaxed">
                            {card.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-6 text-center">
        <p className="text-slate-500 text-sm">
          Powered by{' '}
          <a href="/" className="text-amber-400 hover:text-amber-300 font-medium">
            WillKart
          </a>
        </p>
      </footer>
    </div>
  );
};

export default ClientLogin;
